import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import {
  RefreshCw, Zap, AlertCircle, Activity, BarChart3,
} from 'lucide-react';
import { API } from '../../App';
import { useAlert } from '../../context/AlertContext';

/**
 * M3 Electricity Dashboard — kWh consumption, kWh per unit production,
 * peak hourly demand, DG (diesel) usage %, hourly load curve last 24h,
 * daily load profile by hour-of-day. Reads /api/oim/utilities/electricity.
 */

const PERIOD_OPTIONS = [
  { id: '7d',   label: 'Last 7 days' },
  { id: '30d',  label: 'Last 30 days' },
  { id: '90d',  label: 'Last 90 days' },
  { id: '180d', label: 'Last 180 days' },
];

const fmt = (n, d = 2) => n == null || isNaN(n) ? '—'
  : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtCompact = (n) => {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(2) + 'M';
  if (Math.abs(v) >= 1e3) return (v / 1e3).toFixed(2) + 'K';
  return v.toFixed(0);
};

export default function OimElectricityPanel({ siteId }) {
  const { showError } = useAlert();
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ site_id: siteId, period });
      const res = await axios.get(`${API}/oim/utilities/electricity?${params}`);
      setData(res.data);
    } catch (e) {
      console.error(e);
      showError(e.response?.data?.detail || 'Failed to load electricity dashboard');
    } finally { setLoading(false); }
  }, [siteId, period, showError]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!siteId) return (
    <div className="p-12 text-center text-gray-500">
      <AlertCircle className="w-10 h-10 mx-auto text-gray-300 mb-2"/>
      Select a site to view electricity dashboard.
    </div>
  );

  const k = data?.kpis || {};

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-600"/> Electricity Dashboard
          </h2>
          <div className="text-xs text-gray-500">Consumption, peak demand, load curve, DG share</div>
        </div>
        <div className="flex items-center gap-2">
          <select value={period} onChange={e => setPeriod(e.target.value)} className="px-2 py-1.5 border rounded text-sm">
            {PERIOD_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <button onClick={fetchData} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}/>
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="Electricity Consumption" value={`${fmtCompact(k.electricity_kwh)} kWh`} sub={`${fmt(k.electricity_kwh, 0)} kWh total`} accent="amber" loading={loading}/>
        <Kpi label="kWh / Unit Production" value={k.kwh_per_unit != null ? `${fmt(k.kwh_per_unit, 3)} kWh/${k.production_unit || 'unit'}` : '—'} sub="Energy intensity" accent="blue" loading={loading}/>
        <Kpi label="Peak Hourly Demand" value={`${fmt(k.peak_hour_kwh, 1)} kW`} sub="Max single-hour kWh in period" accent="rose" loading={loading}/>
        <Kpi label="DG Usage %" value={k.dg_usage_pct != null ? `${fmt(k.dg_usage_pct, 1)}%` : '—'} sub={`${fmtCompact(k.dg_kwh)} kWh-eq diesel`} accent="orange" loading={loading}/>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-1 gap-3 mb-6">
        <Kpi label="Power Factor" value="—" sub="Not tracked in OIM today; add power_factor column to oim_energy_hourly to populate" accent="gray" loading={loading}/>
      </div>

      {/* Hourly load curve (last 24h) */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Hourly Load Curve — last 24 hours</h3>
        <p className="text-xs text-gray-500 mb-2">kWh per hour</p>
        {(data?.hourly_load_curve || []).length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.hourly_load_curve}>
              <defs>
                <linearGradient id="elecGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#eab308" stopOpacity={0.5}/>
                  <stop offset="95%" stopColor="#eab308" stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
              <XAxis dataKey="hour" tick={{ fontSize: 10 }} tickFormatter={v => v ? new Date(v).toLocaleTimeString(undefined, { hour: '2-digit' }) : ''}/>
              <YAxis tick={{ fontSize: 10 }}/>
              <Tooltip formatter={v => `${fmt(v, 2)} kWh`} labelFormatter={v => v ? new Date(v).toLocaleString() : ''}/>
              <Area type="monotone" dataKey="kwh" stroke="#eab308" fill="url(#elecGrad)" strokeWidth={2}/>
            </AreaChart>
          </ResponsiveContainer>
        ) : <Empty hint="No hourly electricity data in the last 24h"/>}
      </div>

      {/* Daily load profile */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Daily Load Profile</h3>
        <p className="text-xs text-gray-500 mb-2">Average kWh by hour-of-day across the full period — exposes shift / production rhythms.</p>
        {(data?.daily_profile || []).length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.daily_profile}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
              <XAxis dataKey="hour_of_day" tick={{ fontSize: 10 }} tickFormatter={v => `${String(v).padStart(2, '0')}h`}/>
              <YAxis tick={{ fontSize: 10 }}/>
              <Tooltip formatter={v => `${fmt(v, 2)} kWh`} labelFormatter={v => `Hour ${String(v).padStart(2, '0')}:00`}/>
              <Bar dataKey="avg_kwh" fill="#f59e0b" radius={[2, 2, 0, 0]}/>
            </BarChart>
          </ResponsiveContainer>
        ) : <Empty hint="Need hourly electricity data to build daily profile"/>}
      </div>

      {/* Peak demand trend */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Daily Peak-Hour Demand</h3>
        <p className="text-xs text-gray-500 mb-2">Maximum hourly kWh recorded each day — sharp spikes can indicate equipment cycling or new loads.</p>
        {(data?.peak_demand_trend || []).length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.peak_demand_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
              <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={v => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}/>
              <YAxis tick={{ fontSize: 10 }}/>
              <Tooltip formatter={v => `${fmt(v, 2)} kWh`}/>
              <Line type="monotone" dataKey="peak_kwh" stroke="#dc2626" strokeWidth={2} dot={{ r: 2 }}/>
            </LineChart>
          </ResponsiveContainer>
        ) : <Empty hint="Need hourly electricity data to compute peak demand trend"/>}
      </div>

      {/* Cost trend */}
      {(data?.cost_trend || []).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Daily Total Cost</h3>
          <p className="text-xs text-gray-500 mb-2">Sum of all utility cost rows per day ({data.cost_trend[0]?.currency || 'INR'}). OIM tracks total cost; per-fuel split coming when costs are tagged.</p>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.cost_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}/>
              <YAxis tick={{ fontSize: 10 }}/>
              <Tooltip formatter={v => `${fmt(v, 2)} ${data.cost_trend[0]?.currency || ''}`}/>
              <Line type="monotone" dataKey="total_cost" stroke="#0891b2" strokeWidth={2} dot={{ r: 2 }}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, accent = 'gray', loading }) {
  const accents = {
    amber:   { bg: 'bg-amber-50',   text: 'text-amber-700' },
    orange:  { bg: 'bg-orange-50',  text: 'text-orange-700' },
    blue:    { bg: 'bg-blue-50',    text: 'text-blue-700' },
    rose:    { bg: 'bg-rose-50',    text: 'text-rose-700' },
    gray:    { bg: 'bg-gray-50',    text: 'text-gray-700' },
  };
  const a = accents[accent] || accents.gray;
  if (loading) return (
    <div className={`p-3 rounded-lg border border-gray-200 ${a.bg} animate-pulse`}>
      <div className="h-3 w-20 bg-gray-200 rounded mb-2"/>
      <div className="h-6 w-28 bg-gray-200 rounded"/>
    </div>
  );
  return (
    <div className={`p-3 rounded-lg border border-gray-200 ${a.bg}`}>
      <div className={`text-[10px] font-semibold uppercase tracking-wide ${a.text}`}>{label}</div>
      <div className="text-xl font-bold text-gray-900 mt-1 truncate">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Empty({ hint }) {
  return (
    <div className="flex flex-col items-center justify-center text-center text-gray-400 py-8 text-xs">
      <BarChart3 className="w-8 h-8 text-gray-300 mb-2"/>
      <div>{hint}</div>
    </div>
  );
}
